import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-data-table>` says about itself.
 *
 * ── ONE LIST, FOUR BODIES ────────────────────────────────────────────────────
 * A table is not the only way to draw a list of records. The library has four pieces that take the
 * SAME record set and draw it four ways — this one, `CardGrid`, `KanbanBoard` and `SplitView` — and
 * all four follow the same contract: data in as properties, events out. The example labelled "The
 * orders list" in each of those four files is deliberately the same three records, so the choice
 * between them can be made by looking rather than by reading four pages. `insteadUse` names the
 * other three and says where each one wins.
 *
 * ── ON THE INPUT NAMES ───────────────────────────────────────────────────────
 * `loading-rows`, `empty-label` and `label` are attributes with no property mirror on the class —
 * there is no `el.loadingRows`. They are declared under their hyphenated names because that is the
 * only spelling that actually sets them; declaring a camelCase property name would name something
 * that does not exist.
 *
 * ── THE GRID'S NAME ─────────────────────────────────────────────────────────
 * `label` is the grid's `aria-label`, kept in step whenever it is set, changed or removed (CAOS-648).
 * Removed, it leaves the grid with no name, like a table never given one. It was once read only on
 * connection, so a table already on the page kept whatever name it connected with.
 */

/** The columns of the shared orders fixture — the same list the other three bodies draw. */
const ORDER_COLUMNS = [
  { key: 'reference', label: 'Order', sortable: true },
  { key: 'account', label: 'Account' },
  { key: 'status', label: 'Status' },
  { key: 'total', label: 'Total', align: 'end', sortable: true },
];

/** The plain three-column shape used by the examples that are about something other than columns. */
const PLAIN_COLUMNS = [
  { key: 'reference', label: 'Order' },
  { key: 'account', label: 'Account' },
  { key: 'total', label: 'Total', align: 'end' },
];

const docs: ComponentDocs = {
  aliases: ['table', 'grid', 'data grid', 'list view', 'record list', 'results table'],

  answers: [
    'show a set of records as rows and columns',
    'let somebody sort a list by a column',
    'let somebody tick several records and act on all of them',
    'show a list that is still loading without the page jumping when it arrives',
    'put a totals line under a column of figures',
    'number the rows, so somebody reading a long list can refer to one of them',
  ],

  placements: ['recordPage', 'homePage', 'appPage'],

  inputs: [
    {
      name: 'columns',
      type: 'json',
      description:
        'The columns, in order: each one a cell key, a header label, and optionally sortable, ' +
        'editable, an alignment (numeric columns read end) and a fixed width. Set as a property — ' +
        'an attribute cannot hold a list. An editable column is edited in the grid: its cells draw ' +
        'a control, and the table reports the edit for you to write — it never changes a row ' +
        'itself. A cell that supplies its own node or slot keeps it, so a picklist or a lookup you ' +
        'compose by hand still wins over the declared control.',
      required: true,
    },
    {
      name: 'rows',
      type: 'json',
      default: [],
      description:
        'The records. Each row carries an id, its cells keyed by column key, and optionally ' +
        'selected, disabled or total. A row may also carry children — the rows beneath it — which ' +
        'draw as ordinary rows at their own depth, with the same cells, checkbox and actions as any ' +
        'other. Nothing is fetched here: the access-filtered, formatted set ' +
        'is assembled by the app layer and handed in. An empty list is a real state and draws the ' +
        'empty content rather than an empty frame.',
    },
    {
      name: 'sort',
      type: 'json',
      description:
        'Which column is sorted and which way, as { key, direction }. Setting it moves the header ' +
        'marker and aria-sort; it does NOT reorder the rows. The host reorders and hands back a new ' +
        'rows list.',
      default: null,
    },
    {
      name: 'selectable',
      attribute: 'selectable',
      property: true,
      type: 'boolean',
      description:
        'Add the checkbox column and the tri-state select-all in the header. A row flagged total is ' +
        'never selectable and gets no checkbox.',
      default: false,
    },
    {
      name: 'expandable',
      attribute: 'expandable',
      property: true,
      type: 'boolean',
      description:
        'Lets a reader open and close the rows under a parent row. Every parent still starts OPEN, ' +
        'so nothing a table was given is hidden on arrival. Without this, children still draw at ' +
        'their depth and simply cannot be put away. This is a MODE of this table, not a second ' +
        'hierarchical table: a nested row is an ordinary row, so selection and row actions reach it ' +
        'unchanged. It is the Tree input collapsible under the name a table calls it.',
      default: false,
    },
    {
      name: 'rowNumbers',
      attribute: 'row-numbers',
      property: true,
      type: 'boolean',
      description:
        'Draw a narrow gutter of row numbers, between the checkbox column and the data. The number ' +
        'is the position of the row among the rows SHOWN — the rows not collapsed away — because a ' +
        'table is handed its rows already in display order and is told neither how many there are ' +
        'in total nor which page it has. A paged list that wants positions in the whole result set ' +
        'says where its page begins with rowNumberStart. Scrolling never changes a number: a long ' +
        'table renders only a window of rows, but the numbering is computed over the whole list. ' +
        'A total row keeps the gutter cell and gets no number, since a summary line is not the nth ' +
        'record; a nested row does get one, continuing the sequence, because a nested row is an ' +
        'ordinary row — so closing a parent does move the rows below it up a number.',
      default: false,
    },
    {
      name: 'rowNumberStart',
      attribute: 'row-number-start',
      property: true,
      type: 'number',
      description:
        'The number given to the first row this table holds, so a paged list reads as positions in ' +
        'the result set rather than in the page — the third page at 25 rows a page sets 51. Both ' +
        'numbers are named deliberately: "page 3 of 25" reads as the third of twenty-five pages and ' +
        'fixes no offset. Anything Number cannot read as a finite value of 1 or more falls back to ' +
        '1, since a position a person reads is 1-based. It also sets the number the row checkbox ' +
        'announces, so what is printed and what is spoken agree.',
      default: 1,
    },
    {
      name: 'loading',
      attribute: 'loading',
      type: 'boolean',
      description:
        'The rows have not arrived. Draws placeholder rows in the real column shape instead of the ' +
        'body, so the table does not jump when the data lands.',
      default: false,
    },
    {
      name: 'loadingRows',
      attribute: 'loading-rows',
      type: 'number',
      description: 'How many placeholder rows to draw while loading.',
      default: 3,
    },
    {
      name: 'emptyLabel',
      attribute: 'empty-label',
      type: 'text',
      description: 'The heading of the built-in empty content shown when there are no rows.',
      default: 'No records',
    },
    {
      name: 'label',
      attribute: 'label',
      type: 'text',
      description:
        'The accessible name of the grid — what a screen reader calls this table. Follows the ' +
        'attribute whenever it changes; without it the grid has no name.',
    },
  ],

  events: [
    {
      name: 'caos-sort',
      when: 'A sortable column header is pressed. The header marker moves immediately; the rows do not.',
      carries: '{ column, direction } — the column key, and asc or desc.',
    },
    {
      name: 'caos-cell-edit',
      when:
        "An editable cell's value changes as somebody types, and when Escape puts the declared " +
        'value back. Reported as it happens, for validating or counting unsaved edits — do not ' +
        're-render rows from it, because that destroys the control being typed in.',
      carries: '{ id, column, value, previous } — the row, the column key, what it now reads, and the value the row declared.',
    },
    {
      name: 'caos-cell-commit',
      when:
        'An edit is committed and actually differs: Enter, or leaving the cell. This is the one ' +
        'that means save it. The cell keeps showing the edited value until you write it and the ' +
        'row comes back changed.',
      carries: '{ id, column, value, previous } — the same shape as caos-cell-edit.',
    },
    {
      name: 'caos-row-select',
      when: "One row's checkbox is ticked or cleared.",
      carries: '{ id, selected, selectedIds } — the row, its new state, and every selected row id.',
    },
    {
      name: 'caos-select-all',
      when: 'The header checkbox is ticked or cleared. Disabled rows and total rows are left alone.',
      carries:
        '{ selected, selectedIds } — the new state, and every selected row id afterwards. Rows ' +
        'inside a closed parent are included: a reader who ticks select-all means every row, not ' +
        'every row they happen to be looking at.',
    },
    {
      name: 'caos-row-expand',
      when: 'A parent row is opened or closed. Only an expandable table raises it.',
      carries: '{ id, expanded } — the parent row, and whether its children are now showing.',
    },
  ],

  state: [
    {
      name: 'selectedIds',
      type: 'string[]',
      description:
        'The ids of the rows currently ticked. Selection is driven by the checkboxes and reported by the events; nothing sets it.',
    },
  ],

  slots: [
    {
      name: 'footer',
      takes:
        'The strip under the table, kept outside the horizontal scroller so it stays put while a ' +
        'wide table scrolls. The source describes it as carrying the pagination affordance — and ' +
        'the library has no pagination piece, so there is nothing to put in it yet. Whatever is ' +
        'placed here is laid out as a row with its ends pushed apart, above a top rule.',
    },
    {
      name: 'empty',
      takes:
        'What to show instead of rows when the list is empty. Left alone it holds a default ' +
        'EmptyState headed by empty-label; filling it replaces that, which is how a list says ' +
        'something more useful than "no records".',
    },
    {
      name: '(named by a cell)',
      takes:
        'A cell whose value is the descriptor { slot: "name" } draws a slot of that name in place, ' +
        'so a real piece — a StatusPill in a stage column, a Tag, a ProvenanceMarker — is projected ' +
        'into the cell from the light DOM. The names are chosen by the data rather than by this ' +
        'component, so they cannot be listed here.',
    },
  ],

  tokens: [
    { token: '--caos-color-border-strong', controls: 'The border around the table card.' },
    { token: '--caos-radius-lg', controls: 'The corners of the table card.' },
    { token: '--caos-shadow-card', controls: 'The elevation of the table card.' },
    { token: '--caos-color-surface', controls: 'The ground the table sits on.' },
    { token: '--caos-color-surface-2', controls: 'The hover tint of a row.' },
    { token: '--caos-font', controls: 'The typeface of the cells.' },
    { token: '--caos-text-row', controls: 'The text size of a row.' },
    { token: '--caos-color-text', controls: 'The cell ink.' },
    { token: '--caos-font-mono', controls: 'The header face, and the figures in an end-aligned column.' },
    { token: '--caos-font-weight-label', controls: 'The weight of the column headers.' },
    { token: '--caos-color-text-ghost', controls: 'The header ink at rest.' },
    { token: '--caos-color-border', controls: 'The rule under the header and between the rows.' },
    {
      token: '--caos-color-accent',
      controls: 'The rule above a total row, the links inside cells, and the selected-row tint.',
    },
    { token: '--caos-color-accent-strong', controls: 'The header ink of the column currently sorted.' },
    { token: '--caos-color-text-muted', controls: 'The em dash standing in for an empty cell.' },
    { token: '--caos-font-display', controls: 'The figure on a total row.' },
    { token: '--caos-focus-ring', controls: 'The focus ring on a sort header and on a cell link.' },
    { token: '--caos-radius-sm', controls: 'The corners of that focus ring.' },
    { token: '--caos-space-1', controls: 'The gap between a header label and its sort arrow.' },
    { token: '--caos-space-2', controls: 'The gap between things inside a cell, and the checkbox column inset.' },
    { token: '--caos-space-3', controls: 'The footer padding down, and the gap between footer items.' },
    { token: '--caos-space-4', controls: 'The footer padding across.' },
  ],

  parts: [
    { part: 'scroll', is: 'the box that scrolls when the table is wider than its space' },
    { part: 'table', is: 'the table element' },
    { part: 'head', is: 'the header section' },
    { part: 'header-row', is: 'the row of column headers' },
    { part: 'header-cell', is: 'one column header, the selection column included' },
    { part: 'sort-button', is: 'the control inside a sortable column header' },
    { part: 'select-all', is: 'the checkbox in the header of the selection column' },
    { part: 'body', is: 'the section holding the rows' },
    { part: 'row', is: 'one row' },
    { part: 'cell', is: 'one cell, the selection cell included' },
    { part: 'cell-edit', is: 'the control inside an editable cell' },
    { part: 'row-select', is: 'the checkbox in a row selection cell' },
    { part: 'footer', is: 'the strip under the table holding slotted footer content' },
  ],

  methods: [
    {
      name: 'settleCell',
      description:
        'Tell an editable cell that the save for its last commit has landed, clearing its unsaved marker. ' +
        'Call it on success and only on success: the marker is the difference between what the row declared ' +
        'and what the control holds, so an unsettled cell goes on saying — correctly — that it holds an edit ' +
        'the record does not. It writes nothing into the control and moves neither focus nor the caret, ' +
        'because a cell editor keeps focus through a commit and a value arriving while somebody types the ' +
        'next one would take the caret with it. Returns false when there is no such editable cell drawn — ' +
        'scrolled out of a virtual window, no longer a column, or the table repainted while the save was in ' +
        'flight — which is not a failure and needs no handling. It takes no value on purpose: the cell knows ' +
        'what it committed, and the canonical form a server returns is the one thing that must not be written ' +
        'into a control somebody is still using.',
      args: [
        { name: 'rowId', type: 'string', description: "The row's id, as the commit event reported it." },
        { name: 'columnKey', type: 'string', description: 'The column key, as the commit event reported it.' },
        {
          name: 'saved',
          type: 'string',
          description:
            'The value that was written — the one the commit handed over, not the canonical form a server ' +
            'returned. An answer about an older value cannot then clear the marker for a newer edit.',
        },
      ],
    },
    {
      name: 'refuseCell',
      description:
        'Tell an editable cell that the save for its last commit did NOT land. The pair of settleCell, and ' +
        'not optional: committing records what was reported so the blur after Enter does not report it twice, ' +
        'which means that after a refusal the cell still holds the refused value and a second Enter emits ' +
        'nothing — the person would have to change the text and change it back. This forgets the report, so ' +
        'pressing Enter tries again. Nothing a person can see changes: the typed value stays and so does the ' +
        'unsaved marker, which is true of a cell holding an edit the record does not have. Returns false when ' +
        'no such editable cell is drawn, which needs no handling.',
      args: [
        { name: 'rowId', type: 'string', description: "The row's id, as the commit event reported it." },
        { name: 'columnKey', type: 'string', description: 'The column key, as the commit event reported it.' },
        {
          name: 'refused',
          type: 'string',
          description:
            'The value the save was about, so an answer to an older save cannot discard a newer edit that is ' +
            'still awaiting its own.',
        },
      ],
    },
  ],

  placing:
    'A table is the BODY of a list surface: it goes under the header that names the list and under ' +
    'any filter row, and it fills the width it is given. It brings its own card — border, corners ' +
    'and elevation — so it does not want another panel drawn around it. A wide table scrolls inside ' +
    'itself, so it never widens the page it is on.',

  whenToUse:
    'Several records that are being COMPARED, where the same field across records is the thing ' +
    'being read and the columns line those values up. It is also the only one of the four list ' +
    'bodies that can select rows or carry a totals line.',

  insteadUse: [
    {
      name: 'CardGrid',
      when:
        'The records are being browsed rather than compared: a name, a sub-line and a couple of ' +
        'rollups each, in a grid that reflows on a narrow screen instead of scrolling sideways.',
    },
    {
      name: 'KanbanBoard',
      when:
        'The question is WHERE THE WORK IS rather than what is in the list — the same records stood ' +
        'up in columns by the field that says what stage each one is at.',
    },
    {
      name: 'SplitView',
      when:
        'Somebody works down the list reading one record at a time, and reading one should not cost ' +
        'a trip to its page and back.',
    },
  ],

  accessibility:
    'The table is a real role="grid": each row is a row, each cell a gridcell, a sortable header is ' +
    'a real button carrying aria-sort, a selected row carries aria-selected, and the select-all ' +
    'checkbox goes indeterminate when only some rows are ticked. Focus roams in two dimensions — ' +
    'Arrow Up/Down between rows, Left/Right within a row, Home/End to the ends — and the stops are ' +
    'enumerated through the shadow-piercing focus util, so a control living inside a slotted ' +
    "piece's own shadow root is reached in order. A control in a cell keeps the keys it needs for " +
    'itself — a text box keeps Left/Right and Home/End for its caret while Up/Down still move ' +
    'between rows, and a select keeps Up/Down — so editing in the grid does not cost you the ' +
    "caret. An editable cell's control is named from its column heading and the row's own first " +
    'readable cell, so the controls in a column are told apart by row rather than all announcing ' +
    "the same heading. Left to the author: set label, because a grid " +
    'with no accessible name is announced as a grid and nothing else; give any piece slotted into a ' +
    'cell its own name; actually perform the sort when caos-sort fires, because the header ' +
    'marker says the list is sorted whether or not anybody reordered it; and actually write the ' +
    'value when caos-cell-commit fires, because the cell goes on showing the edit either way and ' +
    'an edit that was never saved looks exactly like one that was.',

  examples: [
    {
      key: 'row-numbers-paged',
      label: 'Numbered rows on the third page',
      summary:
        'The gutter with a declared start. These are three records from the third page of a list ' +
        'showing 25 a page, so they are 51, 52 and 53 rather than 1, 2 and 3 — which is the ' +
        'distinction the gutter has to be told about, because a table cannot see past the rows it ' +
        'was handed. Drop row-number-start and the same three read 1, 2 and 3, which is what an ' +
        'unpaged list wants.',
      placement: {
        component: 'caos-data-table',
        inputs: {
          label: 'Orders',
          'row-numbers': true,
          'row-number-start': 51,
          columns: PLAIN_COLUMNS,
          rows: [
            { id: 'p51', cells: { reference: 'ORD-5102', account: 'Glenmara Foods', total: '3,180' } },
            { id: 'p52', cells: { reference: 'ORD-5103', account: 'Hale & Burnett', total: '9,640' } },
            { id: 'p53', cells: { reference: 'ORD-5104', account: 'Tessaro Freight', total: '1,275' } },
          ],
        },
      },
    },
    {
      key: 'orders',
      label: 'The orders list',
      summary:
        'Three records as rows and columns: two sortable headers, a status column holding real status ' +
        'pills, and the figures aligned to the right. The same three records the other three list ' +
        'bodies draw.',
      placement: {
        component: 'caos-data-table',
        inputs: {
          label: 'Orders',
          columns: ORDER_COLUMNS,
          rows: [
            {
              id: 'o1',
              cells: {
                reference: 'ORD-4471',
                account: 'Meridian Supply',
                status: { slot: 'status-o1' },
                total: '12,400',
              },
            },
            {
              id: 'o2',
              cells: {
                reference: 'ORD-4472',
                account: 'Calder & Finch',
                status: { slot: 'status-o2' },
                total: '3,150',
              },
            },
            {
              id: 'o3',
              cells: {
                reference: 'ORD-4473',
                account: 'Brightwater Group',
                status: { slot: 'status-o3' },
                total: '86,900',
              },
            },
          ],
        },
        children: [
          { component: 'caos-status-pill', inputs: { slot: 'status-o1', intent: 'muted' }, children: ['Draft'] },
          { component: 'caos-status-pill', inputs: { slot: 'status-o2', intent: 'success' }, children: ['Approved'] },
          { component: 'caos-status-pill', inputs: { slot: 'status-o3', intent: 'info' }, children: ['In review'] },
        ],
      },
    },
    {
      key: 'selection',
      label: 'Selecting rows, and a total',
      summary:
        'The same list with the checkbox column on, one row already ticked, and a summary row under ' +
        'it — which carries no checkbox and is left out of select-all.',
      placement: {
        component: 'caos-data-table',
        inputs: {
          label: 'Orders',
          selectable: true,
          columns: PLAIN_COLUMNS,
          rows: [
            {
              id: 'o1',
              cells: { reference: 'ORD-4471', account: 'Meridian Supply', total: '12,400' },
              selected: true,
            },
            { id: 'o2', cells: { reference: 'ORD-4472', account: 'Calder & Finch', total: '3,150' } },
            { id: 'o3', cells: { reference: 'ORD-4473', account: 'Brightwater Group', total: '86,900' } },
            { id: 'sum', cells: { reference: 'Total', account: '', total: '102,450' }, total: true },
          ],
        },
      },
    },
    {
      key: 'loading',
      label: 'Waiting for the rows',
      summary:
        'The rows have not arrived: placeholder lines are drawn in the real column shape, so the ' +
        'table is the same size before and after the data lands.',
      placement: {
        component: 'caos-data-table',
        inputs: {
          label: 'Orders',
          loading: true,
          'loading-rows': 4,
          columns: PLAIN_COLUMNS,
        },
      },
    },
    {
      key: 'empty',
      label: 'Nothing in the list',
      summary:
        'No rows at all: the headers stay and the body holds an EmptyState headed by empty-label, ' +
        'rather than a blank strip under the column names.',
      placement: {
        component: 'caos-data-table',
        inputs: {
          label: 'Orders',
          'empty-label': 'No orders yet',
          columns: PLAIN_COLUMNS,
          rows: [],
        },
      },
    },
  ],
};

export default docs;
